import React, { HTMLAttributes } from 'react';
import './Skeleton.css';

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'shimmer' | 'pulse' | 'none';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  animation = 'shimmer',
  className = '',
  style,
  ...props
}) => {
  const classes = [
    'skeleton',
    `skeleton-${variant}`,
    animation !== 'none' && `skeleton-${animation}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const computedStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    ...style,
  };

  return <div className={classes} style={computedStyle} {...props} />;
};

// Skeleton presets for common use cases
export const SkeletonText: React.FC<{ lines?: number; lastLineWidth?: string }> = ({
  lines = 1,
  lastLineWidth = '75%',
}) => {
  return (
    <div className="skeleton-text-group">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </div>
  );
};

export const SkeletonAvatar: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <Skeleton variant="circular" width={size} height={size} />
);

export const SkeletonButton: React.FC<{ width?: number }> = ({ width = 100 }) => (
  <Skeleton variant="rounded" width={width} height={44} />
);

// Event Card Skeleton
export const SkeletonEventCard: React.FC = () => (
  <div className="skeleton-event-card">
    <div className="skeleton-event-time">
      <Skeleton variant="text" width={40} />
      <Skeleton variant="text" width={30} />
    </div>
    <div className="skeleton-event-content">
      <Skeleton variant="text" width="80%" height={18} />
      <Skeleton variant="text" width="60%" />
      <div className="skeleton-event-tags">
        <Skeleton variant="rounded" width={70} height={24} />
        <Skeleton variant="rounded" width={50} height={24} />
      </div>
    </div>
  </div>
);

// Insight Card Skeleton
export const SkeletonInsightCard: React.FC = () => (
  <div className="skeleton-insight-card">
    <Skeleton variant="text" width="40%" height={14} />
    <Skeleton variant="text" width="90%" height={16} />
    <Skeleton variant="text" width="70%" />
  </div>
);

// Plan Card Skeleton
export const SkeletonPlanCard: React.FC = () => (
  <div className="skeleton-plan-card">
    <Skeleton variant="rounded" className="skeleton-plan-icon" />
    <div className="skeleton-plan-content">
      <Skeleton variant="text" width="60%" height={16} />
      <Skeleton variant="text" width="40%" height={14} />
    </div>
  </div>
);

// Week Summary Skeleton
export const SkeletonWeekSummary: React.FC = () => (
  <div className="skeleton-week-summary">
    <div className="skeleton-week-header">
      <Skeleton variant="text" width={120} height={16} />
      <Skeleton variant="text" width={60} height={14} />
    </div>
    <div className="skeleton-week-events">
      <Skeleton variant="text" width="80%" height={14} />
      <Skeleton variant="text" width="65%" height={14} />
    </div>
  </div>
);

// Home Loading Skeleton - Full page skeleton for initial load
export const SkeletonHomeLoading: React.FC = () => (
  <div className="skeleton-home">
    <div className="skeleton-home-header">
      <Skeleton variant="text" width={180} height={28} />
      <Skeleton variant="text" width={120} height={16} />
    </div>
    <div className="skeleton-home-tabs">
      <Skeleton variant="rounded" className="skeleton-home-tab" />
      <Skeleton variant="rounded" className="skeleton-home-tab" />
      <Skeleton variant="rounded" className="skeleton-home-tab" />
    </div>
    <div className="skeleton-home-section">
      <Skeleton variant="text" width={100} height={16} />
      <div className="skeleton-events-list">
        <SkeletonEventCard />
        <SkeletonEventCard />
        <SkeletonEventCard />
      </div>
    </div>
  </div>
);

// Schedule Loading Skeleton - Just event cards
export const SkeletonScheduleLoading: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="skeleton-events-list">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonEventCard key={i} />
    ))}
  </div>
);

// Family Member Card Skeleton
export const SkeletonFamilyCard: React.FC = () => (
  <div className="skeleton-family-card">
    <div className="skeleton-family-header">
      <Skeleton variant="circular" width={48} height={48} />
      <div className="skeleton-family-info">
        <Skeleton variant="text" width={120} height={18} />
        <Skeleton variant="text" width={80} height={14} />
      </div>
    </div>
  </div>
);

// Settings/Generic Form Skeleton
export const SkeletonFormSection: React.FC = () => (
  <div className="skeleton-form-section">
    <Skeleton variant="text" width={150} height={20} />
    <Skeleton variant="rounded" width="100%" height={44} />
    <Skeleton variant="rounded" width="100%" height={44} />
  </div>
);

// Itinerary Card Skeleton
export const SkeletonItineraryCard: React.FC = () => (
  <div className="skeleton-itinerary-card">
    <Skeleton variant="text" width="70%" height={20} />
    <Skeleton variant="text" width="50%" height={16} />
    <div className="skeleton-itinerary-items">
      <Skeleton variant="text" width="90%" height={14} />
      <Skeleton variant="text" width="85%" height={14} />
      <Skeleton variant="text" width="80%" height={14} />
    </div>
  </div>
);

export default Skeleton;
